import { useState, useRef, useCallback } from 'react';
import api from '../../utils/api';

export default function FileUploader({
  type = 'slides',
  multiple = false,
  accept = 'image/*,.pdf',
  onUpload,
  label = 'Upload File',
  hint = 'PNG, JPG, PDF up to 50MB',
  compact = false,
}) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState([]);
  const inputRef = useRef();

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(`/upload/${type}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.url;
  };

  const handleFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const fileArr = Array.from(files);
    const prog = fileArr.map(f => ({ name: f.name, status: 'pending' }));
    setProgress(prog);
    const results = [];
    for (let i = 0; i < fileArr.length; i++) {
      try {
        prog[i].status = 'uploading';
        setProgress([...prog]);
        const url = await uploadFile(fileArr[i]);
        prog[i].status = 'done';
        prog[i].url = url;
        results.push(url);
        setProgress([...prog]);
      } catch (err) {
        prog[i].status = 'error';
        prog[i].error = err.response?.data?.error || 'Upload failed';
        setProgress([...prog]);
      }
    }
    setUploading(false);
    if (results.length > 0) onUpload && onUpload(multiple ? results : results[0]);
  }, [type, multiple, onUpload]);

  const onDrop = (e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); };
  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  if (compact) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => inputRef.current.click()} disabled={uploading}>
        {uploading ? '⏳ Uploading...' : '📎 Upload File'}
      </button>
      <input ref={inputRef} type="file" accept={accept} multiple={multiple} style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
      {progress.map((p, i) => (
        <span key={i} style={{ fontSize: 12, color: p.status === 'done' ? 'var(--color-success)' : p.status === 'error' ? 'var(--color-error)' : 'var(--color-text-muted)' }}>
          {p.status === 'done' ? '✓' : p.status === 'error' ? '✗' : '...'} {p.name}
        </span>
      ))}
    </div>
  );

  return (
    <div>
      <div
        style={{ border: `2px dashed ${dragging ? 'var(--color-primary)' : 'var(--color-border)'}`, borderRadius: 12, padding: '32px 24px', textAlign: 'center', cursor: uploading ? 'wait' : 'pointer', background: dragging ? 'var(--teal-50)' : 'var(--slate-50)', opacity: uploading ? 0.7 : 1, transition: 'all 0.2s' }}
        onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
        onClick={() => !uploading && inputRef.current.click()}
      >
        <input ref={inputRef} type="file" accept={accept} multiple={multiple} style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
        <div style={{ fontSize: 36, marginBottom: 10 }}>{uploading ? '⏳' : dragging ? '📂' : '📁'}</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--slate-700)', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{uploading ? 'Uploading...' : `Drag & drop or click to browse • ${hint}`}</div>
      </div>
      {progress.length > 0 && (
        <div style={{ marginTop: 12, background: 'white', border: '1px solid var(--color-border)', borderRadius: 8, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {progress.map((p, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: 'var(--slate-700)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
              <span style={{ fontWeight: 600, flexShrink: 0, color: p.status === 'done' ? 'var(--color-success)' : p.status === 'error' ? 'var(--color-error)' : 'var(--color-text-muted)' }}>
                {p.status === 'done' ? '✓ Done' : p.status === 'error' ? `✗ ${p.error}` : '⏳ Uploading...'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}